import { useMemo } from 'react';

import { definedAccessor, xAxisAccessor as x, y0, y1 } from './accessors.js';
import { getAreaStyle } from './area-style.js';
import { AreaCanvas } from '../../../../core/components/canvas/components/AreaCanvas.js';
import { useGridPlotSize } from '../../../../core/components/chart-grid/hooks/useGridPlotSize.js';
import {
  TimeseriesDatapoint,
  TimeseriesUnit,
} from '../../../../core/types/timeseries.js';
import { isIsolatedPoint } from '../../../../core/utils/is-isolated-point.js';
import { safeGet } from '../../../../core/utils/safe-get.js';
import { useSeriesCurve } from '../../../hooks/useSeriesCurve.js';
import { useTimeseriesFilteredData } from '../../../hooks/useTimeseriesFilteredData.js';
import { useYScales } from '../../../hooks/useYScales.js';
import { TimeseriesAreaSlotProps } from '../../../types/timeseries.js';
import { getYScaleByUnit } from '../../../utils/yAxis.js';
import { useDisplayConfigContext } from '../DisplayConfigContext.js';
import { BaseDisplayProps } from '../shared/types.js';
import { getClampedTimeScale } from '../shared/utils/clamp-value.js';
import { shouldDisplayPoints } from '../shared/utils/display-points.js';

export interface AreaChartDisplayProps extends BaseDisplayProps {
  series: TimeseriesAreaSlotProps[];
}

/*
 * Displays the passed series as stacked area chart.
 * The series are stacked based on their order, starting with the first series rendered at the bottom.
 */
export const AreaChartDisplay = ({
  series: timeseries,
  xScale,
}: AreaChartDisplayProps) => {
  const yScale = useYScales();
  const { highlightedSeriesIds } = useDisplayConfigContext();
  const curve = useSeriesCurve();

  const { width, height } = useGridPlotSize();
  const { seriesMap, datapointsMap } = useTimeseriesFilteredData();

  const areaSeries = useMemo(() => [...timeseries].reverse(), [timeseries]);

  const yScaleByUnit = (unit: TimeseriesUnit) => getYScaleByUnit(unit, yScale);

  const clampedScale = getClampedTimeScale(xScale);

  const isolatedAccessor =
    (displayPoints: boolean) =>
    (datapoints: TimeseriesDatapoint[], index: number) =>
      displayPoints ||
      isIsolatedPoint(datapoints, index, (point) =>
        definedAccessor(point, datapointsMap),
      );

  return (
    <div data-testid="area-chart-display">
      {areaSeries.map((series) => {
        const seriesMetadata = safeGet(seriesMap, series);

        const { color, stroke, opacity } = getAreaStyle(
          seriesMetadata,
          highlightedSeriesIds,
        );

        const strokeColorAccessor = stroke ? () => stroke : undefined;

        const displayPoints = !!shouldDisplayPoints(
          seriesMetadata.pointsDisplay,
          width,
          series.datapoints.length,
        );

        return (
          <AreaCanvas
            key={seriesMetadata.id}
            data={series.datapoints}
            canvasWidth={width}
            canvasHeight={height}
            x={x(xScale, datapointsMap, clampedScale)}
            y0={y0(yScaleByUnit, seriesMetadata, datapointsMap)}
            y1={y1(yScaleByUnit, seriesMetadata, datapointsMap)}
            defined={(point) => definedAccessor(point, datapointsMap)}
            displayScatterPlot={isolatedAccessor(displayPoints)}
            color={() => color}
            opacity={opacity}
            strokeColor={strokeColorAccessor}
            curve={curve}
            enableDotOverflow
          />
        );
      })}
    </div>
  );
};

AreaChartDisplay['displayName'] = 'AreaChartDisplay';
